import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Gavel, Check, X, Clock } from "lucide-react";

// THE VERDICT, ADDED UP. His instruction: *"the verdict, recorded from the ✓verify chip"* —
// ONE record across trucks and parts. TWO answers only: he removed "can't tell" on 2026-10-13
// (*"If they can't read it, they don't verify it"*), so an unreadable stamp is just an unverified
// field, which already blocks the part.
//
// AND THE LATER TRUTH COUNTS (same instruction): *"DO count mistakes found LATER: if a verified
// number is corrected in Admin Review, or when someone pulls the part and finds it wrong, record
// that as WRONG — so the AI's score stays honest, not just based on the first look."* A late
// correction replaces the first look on that number; the old row stays on the record, uncounted.
//
// Owner/admin only and read-only, on the AI screen he already has — no new screen. It is
// deliberately blunt about what it does NOT count: the old per-screen logs recorded "accepted"
// whatever happened, so they are named and left out.
const TILES = [
  { k: "right", label: "Right", Icon: Check, cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  { k: "wrong", label: "Wrong — the number was corrected", Icon: X, cls: "border-red-200 bg-red-50 text-[#B01E28]" },
  { k: "found_later", label: "Of those, found later", Icon: Clock, cls: "border-amber-200 bg-amber-50 text-[#E0781A]" },
];

const SURFACE = { parts: "Parts — numbers read off a tag", bulk_fix: "Parts — found later, in Bulk-Fix", truck_spec: "Trucks — spec cells", truck_components: "Trucks — number grouping" };

export function AiAccuracyCard() {
  const [a, setA] = useState(null);

  useEffect(() => {
    api.get("/ai/accuracy").then(({ data }) => setA(data)).catch(() => setA(false));
  }, []);

  if (a === false) return null;
  const t = a?.totals;

  const Split = ({ title, rows }) => {
    const keys = Object.keys(rows || {}).filter((k) => (rows[k]?.total || 0) > 0);
    if (!keys.length) return null;
    return (
      <>
        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-4 mb-1">{title}</div>
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-slate-400">
              <th className="text-left pb-1">&nbsp;</th>
              <th className="px-1 pb-1 text-center">Right</th>
              <th className="px-1 pb-1 text-center">Wrong</th>
              <th className="px-1 pb-1 text-center">Found later</th>
              <th className="pb-1 text-right">Right</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k} className="border-t border-slate-100">
                <td className="py-1.5 pr-2 text-slate-700 truncate max-w-[220px]">{SURFACE[k] || k}</td>
                <td className="py-1.5 px-1 text-center font-mono text-slate-600">{rows[k].right}</td>
                <td className="py-1.5 px-1 text-center font-mono text-slate-600">{rows[k].wrong}</td>
                <td className="py-1.5 px-1 text-center font-mono text-slate-600">{rows[k].found_later}</td>
                <td className="py-1.5 pl-2 text-right font-mono font-bold text-slate-800">
                  {rows[k].right_share == null ? "—" : `${rows[k].right_share}%`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  };

  return (
    <div data-testid="ai-accuracy-card" className="rounded-xl border border-slate-200 bg-white p-4 mt-4">
      <div className="flex items-center gap-2 mb-1">
        <Gavel className="w-5 h-5 text-violet-600" />
        <div className="font-bold">Was the AI right? — the verdict</div>
        <Badge className="bg-slate-100 text-slate-500 text-[10px] ml-1">admin · read-only</Badge>
      </div>
      <div className="text-xs text-slate-500 mb-3">
        Recorded when somebody taps ✓verify, or types their own characters instead — on parts and on
        trucks, in one record. A number corrected later counts as wrong and replaces the first look.
        {a?.measuring_since
          ? <> Measuring since <span className="font-mono">{String(a.measuring_since).slice(0, 10)}</span>.</>
          : null}
      </div>

      {!a ? (
        <div className="font-mono text-xs text-slate-400">LOADING…</div>
      ) : t?.total === 0 ? (
        <div data-testid="ai-accuracy-empty" className="text-xs text-slate-500">
          Nothing recorded yet. The first verdict lands the next time somebody verifies a number the
          AI read — or corrects one.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-2" data-testid="ai-accuracy-tiles">
            {TILES.map(({ k, label, Icon, cls }) => (
              <div key={k} data-testid={`ai-accuracy-tile-${k}`} className={`rounded-xl border p-3 ${cls}`}>
                <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
                  <Icon className="w-3 h-3" />{label}
                </div>
                <div className="font-mono text-2xl font-bold mt-1">{t[k]}</div>
              </div>
            ))}
          </div>
          <div className="text-[12px] text-slate-600 mt-2" data-testid="ai-accuracy-headline">
            {t.right_share == null
              ? "Nothing judged yet."
              : <>Of the {t.scored} read{t.scored === 1 ? "" : "s"} on the record, the AI was right <b className="font-mono">{t.right_share}%</b> of the time
                  {t.found_later > 0
                    ? <> — and <b className="font-mono">{t.found_later}</b> of the wrong ones only turned up later, after somebody had already verified the number.</>
                    : <>. Nothing has come back wrong after being verified.</>}</>}
          </div>

          <Split title="By screen" rows={a.by_surface} />
          <Split title="By how sure the AI said it was" rows={a.by_confidence} />
          <Split title="By field" rows={a.by_field} />

          {(a.unclear_characters?.flagged?.total > 0 || a.unclear_characters?.clean?.total > 0) && (
            <div className="rounded-lg bg-slate-50 px-3 py-2 mt-4 text-[12px] text-slate-600" data-testid="ai-accuracy-unclear">
              <b>The orange unclear-character line, scored:</b> when the AI flagged characters it was
              right <span className="font-mono">{a.unclear_characters.flagged.right_share ?? "—"}%</span> of
              {" "}{a.unclear_characters.flagged.scored} read{a.unclear_characters.flagged.scored === 1 ? "" : "s"};
              when it flagged nothing, <span className="font-mono">{a.unclear_characters.clean.right_share ?? "—"}%</span> of
              {" "}{a.unclear_characters.clean.scored}. That is the number that says whether the warning is earning its place.
            </div>
          )}

          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mt-4 mb-1">The last few</div>
          <div className="space-y-1" data-testid="ai-accuracy-recent">
            {(a.recent || []).map((r, i) => (
              <div key={i} className={`flex items-center gap-2 text-[11px] rounded-lg px-2 py-1.5 ${r.superseded ? "bg-white border border-dashed border-slate-200 opacity-60" : "bg-slate-50"}`}>
                <span className={`font-bold uppercase tracking-wider ${r.verdict === "right" ? "text-emerald-700" : "text-[#B01E28]"}`}>
                  {r.verdict}
                </span>
                {r.late && <span className="text-[9px] font-bold uppercase tracking-wider text-[#E0781A] bg-amber-50 border border-amber-200 rounded px-1">found later</span>}
                <span className="font-mono text-slate-700">{r.ai_value || "—"}</span>
                {r.person_value && <span className="font-mono text-slate-500">→ {r.person_value}</span>}
                <span className="text-slate-400 truncate">{r.subject_code || ""} · {r.field || ""}</span>
                {r.superseded && <span className="text-[9px] uppercase tracking-wider text-slate-400">replaced by a later correction</span>}
                <span className="ml-auto text-slate-400 font-mono">{String(r.at || "").slice(0, 16).replace("T", " ")}</span>
              </div>
            ))}
          </div>

          <div className="text-[11px] text-slate-400 mt-3">{a.note}</div>
        </>
      )}
    </div>
  );
}
